import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    ArrowLeft,
    CheckCircle,
    Flame,
    TrendingUp,
    Calendar,
    Target,
    BarChart3,
    Clock,
    Award,
} from 'lucide-react';
import { useAppState } from '../../contexts/AppContext';

// Date range presets
type DateRange = 7 | 30 | 90 | 'all';

export default function AppAnalytics() {
    const navigate = useNavigate();
    const { tasks, projects, events } = useAppState();
    const [dateRange, setDateRange] = useState<DateRange>(30);

    // Calculate analytics data
    const analytics = useMemo(() => {
        const now = new Date();
        const today = now.toISOString().split('T')[0];

        // Helper to get date N days ago
        const getDaysAgo = (days: number) => {
            const date = new Date(now);
            date.setDate(date.getDate() - days);
            return date.toISOString().split('T')[0];
        };

        // Get range start date
        const rangeStart = dateRange === 'all' ? null : getDaysAgo(dateRange);

        // All completed tasks (filtered by range)
        const completedTasks = tasks.filter(t =>
            t.completed && (rangeStart === null || (t.completedAt && t.completedAt >= rangeStart))
        );
        const incompleteTasks = tasks.filter(t => !t.completed);

        // Tasks completed in last 7 days (or range if smaller)
        const weekAgo = getDaysAgo(7);
        const completedThisWeek = completedTasks.filter(t =>
            t.completedAt && t.completedAt >= weekAgo
        );

        // Tasks completed today
        const completedToday = completedTasks.filter(t =>
            t.completedAt && t.completedAt.startsWith(today)
        );

        // Completion rate
        const totalTasks = tasks.length;
        const completionRate = totalTasks > 0
            ? Math.round((completedTasks.length / totalTasks) * 100)
            : 0;

        // Calculate streak (consecutive days with at least one completion)
        let streak = 0;
        let checkDate = new Date(now);

        // Check if today has completions, if not start from yesterday
        if (completedToday.length === 0) {
            checkDate.setDate(checkDate.getDate() - 1);
        }

        for (let i = 0; i < 365; i++) {
            const dateStr = checkDate.toISOString().split('T')[0];
            const hasCompletion = completedTasks.some(t =>
                t.completedAt && t.completedAt.startsWith(dateStr)
            );

            if (hasCompletion) {
                streak++;
                checkDate.setDate(checkDate.getDate() - 1);
            } else {
                break;
            }
        }

        // Calculate daily completions for last 7 days
        const dailyCompletions: { day: string; count: number; date: string }[] = [];
        const dayNames = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

        for (let i = 6; i >= 0; i--) {
            const date = new Date(now);
            date.setDate(date.getDate() - i);
            const dateStr = date.toISOString().split('T')[0];
            const count = completedTasks.filter(t =>
                t.completedAt && t.completedAt.startsWith(dateStr)
            ).length;

            dailyCompletions.push({
                day: dayNames[date.getDay()],
                count,
                date: dateStr,
            });
        }

        // Average tasks per day (last 30 days)
        const monthAgo = getDaysAgo(30);
        const completedLastMonth = completedTasks.filter(t =>
            t.completedAt && t.completedAt >= monthAgo
        );
        const avgPerDay = completedLastMonth.length > 0
            ? (completedLastMonth.length / 30).toFixed(1)
            : '0';

        // Project breakdown
        const projectStats = projects.map(project => {
            const projectTasks = tasks.filter(t => t.projectId === project.id);
            const completed = projectTasks.filter(t => t.completed).length;
            const total = projectTasks.length;
            const rate = total > 0 ? Math.round((completed / total) * 100) : 0;

            return {
                ...project,
                totalTasks: total,
                completedTasks: completed,
                completionRate: rate,
            };
        }).filter(p => p.totalTasks > 0);

        // Find best day of week
        const dayTotals = [0, 0, 0, 0, 0, 0, 0];
        completedTasks.forEach(t => {
            if (t.completedAt) {
                const date = new Date(t.completedAt);
                dayTotals[date.getDay()]++;
            }
        });
        const maxDayIndex = dayTotals.indexOf(Math.max(...dayTotals));
        const bestDay = dayNames[maxDayIndex];

        // Most active project
        const mostActiveProject = projectStats.length > 0
            ? projectStats.reduce((a, b) => a.totalTasks > b.totalTasks ? a : b)
            : null;

        // Priority breakdown
        const highPriorityCompleted = completedTasks.filter(t => t.priority === 'high').length;
        const highPriorityTotal = tasks.filter(t => t.priority === 'high').length;

        return {
            totalCompleted: completedTasks.length,
            completedThisWeek: completedThisWeek.length,
            completedToday: completedToday.length,
            incomplete: incompleteTasks.length,
            completionRate,
            streak,
            avgPerDay,
            dailyCompletions,
            projectStats,
            bestDay,
            mostActiveProject,
            highPriorityCompleted,
            highPriorityTotal,
            totalEvents: events.length,
        };
    }, [tasks, projects, events, dateRange]);

    // Get max for chart scaling
    const maxDaily = Math.max(...analytics.dailyCompletions.map(d => d.count), 1);

    // Date range options
    const rangeOptions: { value: DateRange; label: string }[] = [
        { value: 7, label: '7 Days' },
        { value: 30, label: '30 Days' },
        { value: 90, label: '90 Days' },
        { value: 'all', label: 'All Time' },
    ];

    return (
        <div className="min-h-screen bg-[#F5F8FA] dark:bg-dark-bg pb-24">
            {/* Header */}
            <div className="bg-white dark:bg-dark-card border-b border-[#DCE4EA] dark:border-dark-border px-4 py-4">
                <div className="flex items-center gap-3">
                    <button
                        onClick={() => navigate(-1)}
                        className="p-2 -ml-2 hover:bg-gray-100 rounded-full transition-colors"
                    >
                        <ArrowLeft className="w-5 h-5 text-[#1A2832]" />
                    </button>
                    <h1 className="text-xl font-semibold text-[#1A2832] dark:text-white">Analytics</h1>
                </div>

                {/* Date Range Filter Pills */}
                <div className="flex gap-2 mt-3 overflow-x-auto pb-1">
                    {rangeOptions.map((option) => (
                        <button
                            key={option.value}
                            onClick={() => setDateRange(option.value)}
                            className={`px-4 py-2 rounded-full text-sm font-medium whitespace-nowrap transition-all ${dateRange === option.value
                                ? 'bg-[#0D5F6B] text-white'
                                : 'bg-[#F5F8FA] dark:bg-dark-elevated text-[#4A5C6A] dark:text-dark-text-secondary hover:bg-[#EEF3F6]'
                                }`}
                        >
                            {option.label}
                        </button>
                    ))}
                </div>
            </div>

            <div className="p-4 space-y-4">
                {/* Overview Stats Row */}
                <div className="grid grid-cols-2 gap-3">
                    <StatCard
                        icon={CheckCircle}
                        value={analytics.totalCompleted.toString()}
                        label="Tasks Completed"
                        sublabel={dateRange === 'all' ? 'All time' : `Last ${dateRange} days`}
                        color="#0D5F6B"
                    />
                    <StatCard
                        icon={Target}
                        value={`${analytics.completionRate}%`}
                        label="Completion Rate"
                        sublabel={`${analytics.incomplete} remaining`}
                        color="#2A7A85"
                    />
                    <StatCard
                        icon={Flame}
                        value={analytics.streak.toString()}
                        label="Day Streak"
                        sublabel={analytics.streak > 0 ? "Keep it going!" : "Start today!"}
                        color="#E85D04"
                    />
                    <StatCard
                        icon={TrendingUp}
                        value={analytics.avgPerDay}
                        label="Avg per Day"
                        sublabel="Last 30 days"
                        color="#7B8C9B"
                    />
                </div>

                {/* Weekly Activity Chart */}
                <div className="bg-white dark:bg-dark-card rounded-2xl p-4 shadow-[0_4px_16px_rgba(13,95,107,0.08)]">
                    <div className="flex items-center gap-2 mb-4">
                        <BarChart3 className="w-5 h-5 text-[#0D5F6B]" />
                        <h2 className="text-lg font-semibold text-[#1A2832] dark:text-white dark:text-white">Weekly Activity</h2>
                    </div>
                    <div className="flex items-end justify-between gap-2 h-32">
                        {analytics.dailyCompletions.map((day, index) => (
                            <div key={index} className="flex-1 flex flex-col items-center gap-2">
                                <div className="w-full flex justify-center">
                                    <div
                                        className="w-8 rounded-t-lg transition-all duration-300"
                                        style={{
                                            height: `${Math.max((day.count / maxDaily) * 80, 4)}px`,
                                            backgroundColor: day.count > 0 ? '#0D5F6B' : '#DCE4EA',
                                        }}
                                    />
                                </div>
                                <span className="text-xs text-[#7B8C9B] dark:text-dark-text-muted">{day.day}</span>
                                {day.count > 0 && (
                                    <span className="text-xs font-semibold text-[#0D5F6B]">{day.count}</span>
                                )}
                            </div>
                        ))}
                    </div>
                    <div className="mt-4 pt-3 border-t border-[#DCE4EA]">
                        <p className="text-sm text-[#7B8C9B]">
                            <span className="font-semibold text-[#0D5F6B]">{analytics.completedThisWeek}</span> tasks completed this week
                        </p>
                    </div>
                </div>

                {/* AI Insights */}
                <div className="bg-gradient-to-br from-[#0D5F6B] to-[#2A7A85] rounded-2xl p-4 text-white">
                    <div className="flex items-center gap-2 mb-3">
                        <Award className="w-5 h-5" />
                        <h2 className="text-lg font-semibold">Insights</h2>
                    </div>
                    <div className="space-y-3">
                        <InsightRow
                            emoji="📅"
                            text={`Your best day is ${analytics.bestDay} — you get the most done then!`}
                        />
                        {analytics.mostActiveProject && (
                            <InsightRow
                                emoji={analytics.mostActiveProject.icon || '📁'}
                                text={`${analytics.mostActiveProject.name} is your most active project with ${analytics.mostActiveProject.totalTasks} tasks`}
                            />
                        )}
                        {analytics.highPriorityTotal > 0 && (
                            <InsightRow
                                emoji="🎯"
                                text={`You've completed ${analytics.highPriorityCompleted} of ${analytics.highPriorityTotal} high-priority tasks`}
                            />
                        )}
                        {analytics.streak >= 3 && (
                            <InsightRow
                                emoji="🔥"
                                text={`${analytics.streak} day streak! You're building great habits.`}
                            />
                        )}
                    </div>
                </div>

                {/* Project Breakdown */}
                {analytics.projectStats.length > 0 && (
                    <div className="bg-white dark:bg-dark-card rounded-2xl p-4 shadow-[0_4px_16px_rgba(13,95,107,0.08)]">
                        <div className="flex items-center gap-2 mb-4">
                            <Calendar className="w-5 h-5 text-[#0D5F6B]" />
                            <h2 className="text-lg font-semibold text-[#1A2832] dark:text-white dark:text-white">Projects</h2>
                        </div>
                        <div className="space-y-3">
                            {analytics.projectStats.map(project => (
                                <div key={project.id} className="flex items-center gap-3">
                                    <span className="text-xl">{project.icon}</span>
                                    <div className="flex-1">
                                        <div className="flex justify-between items-center mb-1">
                                            <span className="text-sm font-medium text-[#1A2832]">
                                                {project.name}
                                            </span>
                                            <span className="text-xs text-[#7B8C9B] dark:text-dark-text-muted">
                                                {project.completedTasks}/{project.totalTasks}
                                            </span>
                                        </div>
                                        <div className="h-2 bg-[#DCE4EA] rounded-full overflow-hidden">
                                            <div
                                                className="h-full rounded-full transition-all duration-500"
                                                style={{
                                                    width: `${project.completionRate}%`,
                                                    backgroundColor: project.color,
                                                }}
                                            />
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                )}

                {/* Quick Stats Footer */}
                <div className="grid grid-cols-3 gap-3">
                    <div className="bg-white dark:bg-dark-card rounded-xl p-3 text-center shadow-[0_2px_8px_rgba(13,95,107,0.06)]">
                        <Clock className="w-5 h-5 mx-auto mb-1 text-[#7B8C9B]" />
                        <div className="text-lg font-semibold text-[#1A2832] dark:text-white dark:text-white">{analytics.completedToday}</div>
                        <div className="text-xs text-[#7B8C9B] dark:text-dark-text-muted">Today</div>
                    </div>
                    <div className="bg-white dark:bg-dark-card rounded-xl p-3 text-center shadow-[0_2px_8px_rgba(13,95,107,0.06)]">
                        <Calendar className="w-5 h-5 mx-auto mb-1 text-[#7B8C9B]" />
                        <div className="text-lg font-semibold text-[#1A2832] dark:text-white dark:text-white">{analytics.totalEvents}</div>
                        <div className="text-xs text-[#7B8C9B] dark:text-dark-text-muted">Events</div>
                    </div>
                    <div className="bg-white dark:bg-dark-card rounded-xl p-3 text-center shadow-[0_2px_8px_rgba(13,95,107,0.06)]">
                        <Target className="w-5 h-5 mx-auto mb-1 text-[#7B8C9B]" />
                        <div className="text-lg font-semibold text-[#1A2832] dark:text-white dark:text-white">{analytics.incomplete}</div>
                        <div className="text-xs text-[#7B8C9B] dark:text-dark-text-muted">Pending</div>
                    </div>
                </div>
            </div>
        </div>
    );
}

// Stat Card Component
interface StatCardProps {
    icon: React.ElementType;
    value: string;
    label: string;
    sublabel: string;
    color: string;
}

function StatCard({ icon: Icon, value, label, sublabel, color }: StatCardProps) {
    return (
        <div className="bg-white dark:bg-dark-card rounded-xl p-4 shadow-[0_4px_16px_rgba(13,95,107,0.08)]">
            <div className="flex items-center gap-2 mb-2">
                <div
                    className="w-8 h-8 rounded-lg flex items-center justify-center"
                    style={{ backgroundColor: `${color}15` }}
                >
                    <Icon className="w-4 h-4" style={{ color }} />
                </div>
            </div>
            <div className="text-2xl font-bold text-[#1A2832]">{value}</div>
            <div className="text-sm font-medium text-[#4A5C6A]">{label}</div>
            <div className="text-xs text-[#7B8C9B] dark:text-dark-text-muted">{sublabel}</div>
        </div>
    );
}

// Insight Row Component
function InsightRow({ emoji, text }: { emoji: string; text: string }) {
    return (
        <div className="flex items-start gap-3">
            <span className="text-lg">{emoji}</span>
            <p className="text-sm text-white/90">{text}</p>
        </div>
    );
}
